import axiosPrivate from 'config/axiosPrivate';
import { axiosTypixKeywordAPI } from 'config/endpoints';

const EMPTY_CONFIG_BODY = {
  filterDomainLists: [],
  seoCompetitors: [],
  benchmarkCompetitors: [],
  includeTrackUrls: [],
  excludeSubdomains: [],
  majorCompetitors: [],
};

const COUNTRY_CODE_TO_ENUM = {
  FR: 'FRANCE',
  FRANCE: 'FRANCE',
  ES: 'SPAIN',
  SPAIN: 'SPAIN',
  ESPAGNE: 'SPAIN',
  ESPANA: 'SPAIN',
  CH: 'SWITZERLAND',
  SWITZERLAND: 'SWITZERLAND',
  SUISSE: 'SWITZERLAND',
  BE: 'BELGIUM',
  BELGIUM: 'BELGIUM',
  BELGIQUE: 'BELGIUM',
  DE: 'GERMANY',
  GERMANY: 'GERMANY',
  ALLEMAGNE: 'GERMANY',
  US: 'UNITED_STATES',
  UNITED_STATES: 'UNITED_STATES',
  UK: 'UNITED_KINGDOM_OF_GREAT_BRITAIN_AND_NORTHERN_IRELAND',
  GB: 'UNITED_KINGDOM_OF_GREAT_BRITAIN_AND_NORTHERN_IRELAND',
  IT: 'ITALY',
  ITALY: 'ITALY',
  ITALIE: 'ITALY',
  PT: 'PORTUGAL',
  PORTUGAL: 'PORTUGAL',
};

export const KEYWORD_STATUS = {
  IN_PROCESS: 'IN_PROCESS',
  DONE: 'DONE',
  FAILED: 'FAILED',
};

export const IN_PROCESS_STATUSES = new Set([KEYWORD_STATUS.IN_PROCESS]);
export const FAILED_STATUSES = new Set([KEYWORD_STATUS.FAILED]);

/** Map any API status to one of the 3 UI statuses. */
export function normalizeKeywordStatus(status) {
  const raw = String(status || '').trim().toUpperCase();
  if (!raw) return null;
  if (raw === KEYWORD_STATUS.DONE) return KEYWORD_STATUS.DONE;
  if (raw === KEYWORD_STATUS.FAILED || raw === 'FAILED_NOT_ENOUGH_URL') {
    return KEYWORD_STATUS.FAILED;
  }
  if (
    raw === KEYWORD_STATUS.IN_PROCESS ||
    raw === 'IN_PROCESS_FROM_UI' ||
    raw === 'WAITING_PUT_DOMAINS' ||
    raw === 'PAUSED'
  ) {
    return KEYWORD_STATUS.IN_PROCESS;
  }
  return KEYWORD_STATUS.IN_PROCESS;
}

export function getStatusLabel(status) {
  const normalized = normalizeKeywordStatus(status);
  if (normalized === KEYWORD_STATUS.IN_PROCESS) return 'En cours';
  if (normalized === KEYWORD_STATUS.DONE) return 'Analyse terminée';
  if (normalized === KEYWORD_STATUS.FAILED) return 'Erreur – à relancer';
  return '—';
}

const typixUrl = (path) => `${axiosTypixKeywordAPI}${path}`;

/** Build query string — keep websiteUrl readable (not percent-encoded). */
function buildTypixQuery(params) {
  return Object.entries(params)
    .filter(([, value]) => value != null && value !== '')
    .map(([key, value]) => {
      const raw = String(value);
      if (key === 'websiteUrl') {
        return `${key}=${raw}`;
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(raw)}`;
    })
    .join('&');
}

export function mapCountryToApiEnum(country) {
  if (country == null || country === '') return null;

  let raw = '';
  if (typeof country === 'string') {
    raw = country.trim().toUpperCase();
  } else {
    raw = String(
      country.code ||
      country.isoCode ||
      country.countryCode ||
      country.name ||
      country.label ||
      country.country ||
      ''
    )
      .trim()
      .toUpperCase();
  }

  if (!raw) return null;

  const normalized = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_');

  if (COUNTRY_CODE_TO_ENUM[raw]) return COUNTRY_CODE_TO_ENUM[raw];
  if (COUNTRY_CODE_TO_ENUM[normalized]) return COUNTRY_CODE_TO_ENUM[normalized];

  // Already an enum like SPAIN / FRANCE
  return normalized;
}

export function mapLanguageToApiEnum(language) {
  if (!language) return null;

  const LANGUAGE_TO_ENUM = {
    // English
    EN: 'ENGLISH',
    EN_US: 'ENGLISH',
    EN_GB: 'ENGLISH',
    ENGLISH: 'ENGLISH',
    ANGLAIS: 'ENGLISH',
    // French
    FR: 'FRENCH',
    FR_FR: 'FRENCH',
    FRENCH: 'FRENCH',
    FRANCAIS: 'FRENCH',
    FRANÇAIS: 'FRENCH',
    // German
    DE: 'GERMAN',
    GERMAN: 'GERMAN',
    ALLEMAND: 'GERMAN',
    // Spanish
    ES: 'SPANISH',
    SPANISH: 'SPANISH',
    ESPAGNOL: 'SPANISH',
    // Italian
    IT: 'ITALIAN',
    ITALIAN: 'ITALIAN',
    ITALIEN: 'ITALIAN',
    // Portuguese
    PT: 'PORTUGUESE',
    PORTUGUESE: 'PORTUGUESE',
    PORTUGAIS: 'PORTUGUESE',
    // Dutch
    NL: 'DUTCH',
    DUTCH: 'DUTCH',
    NEERLANDAIS: 'DUTCH',
    NÉERLANDAIS: 'DUTCH',
    // Polish
    PL: 'POLISH',
    POLISH: 'POLISH',
    POLONAIS: 'POLISH',
    // Romanian
    RO: 'ROMANIAN',
    ROMANIAN: 'ROMANIAN',
    ROUMAIN: 'ROMANIAN',
    // Russian
    RU: 'RUSSIAN',
    RUSSIAN: 'RUSSIAN',
    RUSSE: 'RUSSIAN',
    // Bulgarian
    BG: 'BULGARIAN',
    BULGARIAN: 'BULGARIAN',
    BULGARE: 'BULGARIAN',
    // Korean
    KO: 'KOREAN',
    KOREAN: 'KOREAN',
    COREEN: 'KOREAN',
    CORÉEN: 'KOREAN',
    // Estonian
    ET: 'ESTONIAN',
    ESTONIAN: 'ESTONIAN',
    ESTONIEN: 'ESTONIAN',
    // Finnish
    FI: 'FINNISH',
    FINNISH: 'FINNISH',
    FINNOIS: 'FINNISH',
    // Greek
    EL: 'GREEK',
    GREEK: 'GREEK',
    GREC: 'GREEK',
    // Czech
    CS: 'CZECH',
    CZECH: 'CZECH',
    TCHEQUE: 'CZECH',
    TCHÈQUE: 'CZECH',
  };

  const candidates = [];
  if (typeof language === 'string') {
    candidates.push(language);
  } else {
    // Prefer API-style id (ENGLISH) when present, then labels / codes
    if (typeof language.id === 'string') candidates.push(language.id);
    candidates.push(
      language.language,
      language.name,
      language.label,
      language.abbreviation,
      language.isoCode,
      language.code
    );
  }

  for (const candidate of candidates) {
    if (candidate == null || candidate === '') continue;
    const upper = String(candidate).trim().toUpperCase().replace(/\s+/g, '_');
    const normalized = upper
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    if (LANGUAGE_TO_ENUM[upper]) return LANGUAGE_TO_ENUM[upper];
    if (LANGUAGE_TO_ENUM[normalized]) return LANGUAGE_TO_ENUM[normalized];

    // Already a valid typix enum (e.g. ENGLISH)
    if (/^[A-Z_]+$/.test(normalized) && normalized.length > 2) {
      return normalized;
    }
  }

  return null;
}

export function getWebsiteLanguage(website) {
  return website?.language || website?.langue || null;
}

/** Read country from website object (string or nested). Never defaults. */
export function getWebsiteCountry(website) {
  if (!website) return null;
  const raw =
    website.country ??
    website.pays ??
    website.countryCode ??
    website.country_code ??
    null;
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') {
    return (
      raw.code ||
      raw.isoCode ||
      raw.countryCode ||
      raw.name ||
      raw.label ||
      raw.country ||
      null
    );
  }
  return raw;
}

export const PRODUCTION_LABELS = {
  IA: 'IA',
  HYBRID: 'Hybride',
  HUMAN: 'Humain',
};


export function normalizeProductionLabel(value, fallback = PRODUCTION_LABELS.HYBRID) {
  const raw = String(value || '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (!raw) return fallback;
  if (raw.includes('HUMAN') || raw.includes('HUMAIN')) return PRODUCTION_LABELS.HUMAN;
  if (raw.includes('HYBRID') || raw.includes('HYBRIDE')) return PRODUCTION_LABELS.HYBRID;
  if (raw.includes('IA') || raw.includes('AI')) return PRODUCTION_LABELS.IA;
  return fallback;
}

export function mapDifficultyToProductionType(difficulty) {
  const label = normalizeProductionLabel(difficulty, PRODUCTION_LABELS.IA);
  if (label === PRODUCTION_LABELS.HUMAN) return 'HUMAN';
  if (label === PRODUCTION_LABELS.HYBRID) return 'HYBRID';
  return 'IA';
}

export function normalizeKeywordRows(payload) {
  let list = Array.isArray(payload)
    ? payload
    : payload?.keywords ||
    payload?.results ||
    payload?.verticalKwsResults ||
    payload?.data ||
    null;

  if (!list && payload && typeof payload === 'object') {
    const looksLikeKeywordRow =
      payload.keyword &&
      (payload.searchVolume != null ||
        payload.interestScore != null ||
        payload.volume != null ||
        payload.interest != null ||
        payload.svisScore != null ||
        payload.agent != null ||
        payload.productionType != null);
    list = looksLikeKeywordRow ? [payload] : [];
  }

  if (!Array.isArray(list)) return [];

  return list
    .map((item) => {
      const keyword = String(item?.keyword || item?.kw || '').trim();
      if (!keyword) return null;
      // Status-only payloads (ResultDisplayUi) are not result rows
      if (
        item?.status &&
        item.searchVolume == null &&
        item.interestScore == null &&
        item.volume == null &&
        item.interest == null &&
        item.svisScore == null &&
        !item.agent &&
        !item.productionType
      ) {
        return null;
      }
      const interestRaw = Number(item?.interestScore ?? item?.interest ?? item?.svisScore ?? 0);
      const volumeRaw = Number(item?.searchVolume ?? item?.volume ?? 0);
      const agent = String(
        item?.agent ||
          item?.difficulty ||
          item?.production ||
          item?.productionType ||
          ''
      ).trim();
      return {
        keyword,
        interest: Number.isFinite(interestRaw) ? interestRaw : 0,
        volume: Number.isFinite(volumeRaw) ? Math.round(volumeRaw) : 0,
        difficulty: normalizeProductionLabel(agent),
      };
    })
    .filter(Boolean);
}

export async function saveTypixConfig({ account, websiteUrl, country, language }) {
  const resolvedCountry = mapCountryToApiEnum(country);
  const resolvedLanguage = mapLanguageToApiEnum(language);

  if (!resolvedCountry) {
    throw new Error(`country missing from website: ${JSON.stringify(country)}`);
  }
  if (!resolvedLanguage) {
    throw new Error(`language missing from website: ${JSON.stringify(language)}`);
  }

  const query = buildTypixQuery({
    account,
    websiteUrl,
    country: resolvedCountry,
    language: resolvedLanguage,
  });

  const { data, status } = await axiosPrivate.post(
    `${typixUrl('/api/typix/save-config')}?${query}`,
    EMPTY_CONFIG_BODY,
    {
      headers: { accept: '*/*' },
    }
  );
  if (status !== 200) {
    throw new Error('save-config failed');
  }
  return data;
}

export async function startKeywordExecution({
  account,
  websiteUrl,
  keyword,
  executorPersonEmail,
}) {
  const query = buildTypixQuery({
    account,
    websiteUrl,
    keyword,
    executorPersonEmail,
  });
  const { data, status } = await axiosPrivate.post(
    `${typixUrl('/api/typix/start')}?${query}`,
    null,
    {
      headers: { accept: '*/*' },
    }
  );
  if (status !== 200) {
    throw new Error('start failed');
  }
  return data;
}

export async function getSpecificKeywordResult({ account, websiteUrl, keyword }) {
  const query = buildTypixQuery({ account, websiteUrl, keyword });
  const { data } = await axiosPrivate.get(
    `${typixUrl('/api/typix/get-specific-keyword-result')}?${query}`,
    {
      headers: { accept: 'application/json' },
    }
  );
  return data;
}

export async function getAllKeywordResults({ account, websiteUrl }) {
  const query = buildTypixQuery({ account, websiteUrl });
  try {
    const { data } = await axiosPrivate.get(
      `${typixUrl('/api/typix/get-all-results')}?${query}`,
      {
        headers: { accept: '*/*' },
      }
    );
    return Array.isArray(data) ? data : [];
  } catch (error) {
    if (error?.response?.status === 500) {
      return [];
    }
    throw error;
  }
}

export async function saveKeywordExplorerResults(seedKeyword, rows) {
  const keyword = String(seedKeyword || '').trim();
  if (!keyword) {
    throw new Error('seed keyword missing');
  }
  const body = (Array.isArray(rows) ? rows : []).map((r) => ({
    keyword: String(r?.keyword || '').trim(),
    interestScore: Number(r?.interest ?? r?.interestScore ?? 0) || 0,
    searchVolume: Number(r?.volume ?? r?.searchVolume ?? 0) || 0,
    productionType: mapDifficultyToProductionType(r?.difficulty ?? r?.productionType),
  })).filter((item) => item.keyword);

  if (!body.length) {
    throw new Error('no keywords to save');
  }

  const { data, status } = await axiosPrivate.post(
    `/texts/external/keyword-explorer/keyword=${encodeURIComponent(keyword)}`,
    body
  );
  if (status < 200 || status >= 300) {
    throw new Error('save keyword explorer failed');
  }
  return data;
}

export function extractStatusFromResult(payload) {
  if (!payload) return null;
  if (typeof payload.status === 'string') {
    return normalizeKeywordStatus(payload.status);
  }
  if (Array.isArray(payload) && payload.length > 0) return KEYWORD_STATUS.DONE;
  if (normalizeKeywordRows(payload).length > 0) return KEYWORD_STATUS.DONE;
  return null;
}

export async function runKeywordDiscoveryAnalysis({
  account,
  websiteUrl,
  country,
  language,
  keyword,
  executorPersonEmail,
  pollIntervalMs = 5000,
  shouldContinue,
}) {
  await saveTypixConfig({ account, websiteUrl, country, language });
  const startData = await startKeywordExecution({
    account,
    websiteUrl,
    keyword,
    executorPersonEmail,
  });

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  let lastPayload = startData;
  let status = extractStatusFromResult(startData) || KEYWORD_STATUS.IN_PROCESS;

  while (status === KEYWORD_STATUS.IN_PROCESS) {
    if (shouldContinue && !shouldContinue()) {
      return { status, payload: lastPayload, cancelled: true, rows: [] };
    }
    await wait(pollIntervalMs);
    lastPayload = await getSpecificKeywordResult({ account, websiteUrl, keyword });
    status = extractStatusFromResult(lastPayload) || KEYWORD_STATUS.IN_PROCESS;
  }

  if (status === KEYWORD_STATUS.FAILED) {
    return { status, payload: lastPayload, cancelled: false, rows: [] };
  }

  const rows = normalizeKeywordRows(lastPayload);
  return { status: status || KEYWORD_STATUS.DONE, payload: lastPayload, cancelled: false, rows };
}

export function toCsv(rows) {
  const esc = (v) => '"' + String(v).replace(/"/g, '""') + '"';
  return (
    '\uFEFF' +
    [
      ['Mot-clé', "Score d'intérêt", 'Volume', 'Production'].join(','),
      ...rows.map((r) =>
        [r.keyword, r.interest, r.volume, r.difficulty].map(esc).join(',')
      ),
    ].join('\n')
  );
}

export function downloadFile(name, text, type = 'text/plain;charset=utf-8') {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const diffStyle = (d) => {
  const label = normalizeProductionLabel(d);
  if (label === PRODUCTION_LABELS.IA) {
    return { background: '#eae7fd', color: '#6a5ae0' };
  }
  if (label === PRODUCTION_LABELS.HUMAN) {
    return { background: '#fdeaea', color: '#d64545' };
  }
  return { background: '#fef3e2', color: '#c77d0a' };
};

export const fmt = (n) => (n || 0).toLocaleString('fr-FR');
